import React, { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { usePlans, useCredits } from '../hooks';
import { PaymentMethodModal } from '../components/PaymentMethodModal';
import { PixQrCodeModal } from '../components/PixQrCodeModal';
import type { PlanResponse } from '../types';
import { Icon } from '@/shared/icons';

/**
 * Pagina de selecao de planos.
 *
 * Exibe os planos disponiveis para assinatura com créditos por ciclo,
 * precos mensais/anuais e opcao de metodo de pagamento.
 */
export const PlansPage: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState<PlanResponse | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isUpgradePixModalOpen, setIsUpgradePixModalOpen] = useState(false);

  const { plans, isLoading: isLoadingPlans } = usePlans();
  const { dashboard, changePlanAsync, isChangingPlan } = useCredits();

  const currentPlanId = dashboard?.subscription?.planId;
  const hasSubscription = !!dashboard?.subscription;

  const formatCurrency = (amount: unknown, currency: string = 'BRL') => {
    const num = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
    }).format(num);
  };

  const formatCredits = (credits: unknown) => {
    const num = typeof credits === 'number' && !isNaN(credits) ? credits : 0;
    return new Intl.NumberFormat('pt-BR').format(num);
  };

  /** Safely extract price fields — handles legacy plans where price was a single number */
  const getPlanPrice = (plan: PlanResponse) => {
    const price = plan.price as unknown;
    if (price && typeof price === 'object' && price !== null) {
      const p = price as Record<string, unknown>;
      return {
        monthly: typeof p.monthly === 'number' ? p.monthly : 0,
        yearly: typeof p.yearly === 'number' ? p.yearly : 0,
        currency: typeof p.currency === 'string' ? p.currency : 'BRL',
      };
    }
    // Legacy: price was a single number
    const legacy = typeof price === 'number' ? price : 0;
    return { monthly: legacy, yearly: 0, currency: 'BRL' };
  };

  const handleSelectPlan = async (plan: PlanResponse) => {
    if (hasSubscription) {
      try {
        const result = await changePlanAsync(plan.id);
        if (result.effectiveAt === 'pending_payment') {
          setIsUpgradePixModalOpen(true);
        }
      } catch {
        // Error toast handled by mutation onError
      }
    } else {
      // New subscription — open payment modal
      setSelectedPlan(plan);
      setIsPaymentModalOpen(true);
    }
  };

  const isCurrentPlan = (planId: string) => {
    return currentPlanId === planId;
  };

  const DEFAULT_FEATURES = ['Acesso a plataforma', 'Suporte por email', 'Atualizacoes incluidas'];

  // Sticky DS toolbar — 3 zonas: breadcrumb | centro | acao primaria (contrato DS, igual BillingPage)
  const toolbar = (
    <div
      className="sticky top-0 z-10"
      style={{
        backgroundColor: 'var(--theme-header-background)',
        borderBottom: '1px solid var(--theme-header-border)',
      }}
    >
      <div
        className="flex items-center gap-3 px-4 sm:px-6 md:px-8"
        style={{ minHeight: 56 }}
      >
        {/* Left: breadcrumb (nunca seta voltar) */}
        <nav
          className="flex items-center gap-2 min-w-0"
          aria-label="breadcrumb"
          data-testid="plans-breadcrumb"
        >
          <span
            className="text-sm font-medium truncate"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            Configurações
          </span>
          <Icon
            name="chevron-right"
            className="h-3.5 w-3.5 flex-shrink-0"
            style={{ color: 'var(--theme-muted-text)' }}
          />
          <span
            className="text-base font-semibold tracking-tight truncate"
            style={{ color: 'var(--theme-header-text)' }}
          >
            Planos
          </span>
        </nav>

        {/* Center spacer (contrato DS 3 zonas) */}
        <div className="flex-1" />

        {/* Right: acao primaria unica */}
        <Button
          icon="check"
          onClick={() => window.open('mailto:?subject=Falar com vendas', '_self')}
          className="flex-shrink-0"
          data-testid="plans-contact-sales-button"
        >
          Falar com vendas
        </Button>
      </div>
    </div>
  );

  if (isLoadingPlans) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        {toolbar}
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-96 rounded-2xl"
                style={{ backgroundColor: 'var(--theme-card-border)' }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {toolbar}

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
        {/* Toggle Mensal/Anual (mockup .seg) */}
        <div className="flex justify-center mb-6">
          <div
            className="inline-flex items-center rounded-lg p-[3px] gap-0.5"
            style={{ backgroundColor: 'var(--theme-card-border)' }}
            role="group"
            data-testid="plans-cycle-toggle"
          >
            {([['monthly', 'Mensal'], ['yearly', 'Anual']] as const).map(([key, label]) => {
              const active = billingCycle === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setBillingCycle(key)}
                  className="px-4 h-[30px] rounded-md text-[13px] font-semibold transition-colors whitespace-nowrap"
                  style={{
                    backgroundColor: active ? 'var(--theme-card-background)' : 'transparent',
                    color: active ? 'var(--theme-card-text)' : 'var(--theme-muted-text)',
                    boxShadow: active ? '0 1px 2px rgba(0,0,0,.06)' : 'none',
                  }}
                  data-testid={`plans-cycle-${key}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Plans Grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch"
          data-testid="plans-grid"
        >
          {plans?.map((plan) => {
            const isCurrent = isCurrentPlan(plan.id);
            const isPopular =
              plan.name.toLowerCase().includes('professional') ||
              plan.name.toLowerCase().includes('pro');
            const price = getPlanPrice(plan);
            const isQuote = price.monthly === 0 && price.yearly === 0;
            const features =
              plan.features && plan.features.length > 0 ? plan.features : DEFAULT_FEATURES;
            const showPixButton =
              hasSubscription &&
              dashboard?.subscription?.pendingPlanChange?.type === 'upgrade' &&
              dashboard?.subscription?.pendingPlanChange?.planId === plan.id &&
              !!dashboard?.subscription?.pendingPlanChange?.pendingPaymentId;

            return (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className="relative flex flex-col rounded-2xl p-6 text-center"
                style={{
                  backgroundColor: 'var(--theme-card-background)',
                  border: `1px solid ${isPopular ? 'var(--theme-primary)' : 'var(--theme-card-border)'}`,
                  boxShadow: isPopular
                    ? '0 0 0 1px var(--theme-primary), 0 12px 32px rgba(0,0,0,0.08)'
                    : 'none',
                }}
              >
                {/* Popular badge */}
                {isPopular && (
                  <span
                    className="absolute left-1/2 -translate-x-1/2 -top-3 rounded-full px-4 py-1 text-xs font-bold whitespace-nowrap"
                    style={{
                      backgroundColor: 'var(--theme-primary)',
                      color: 'var(--theme-button-primary-text)',
                    }}
                  >
                    Mais Popular
                  </span>
                )}

                {/* Name */}
                <h3
                  className="text-lg font-bold tracking-tight"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  {plan.name}
                </h3>

                {/* Description */}
                <p
                  className="text-sm mt-1"
                  style={{ color: 'var(--theme-muted-text)', minHeight: 34 }}
                >
                  {plan.description || ' '}
                </p>

                {/* Credits per cycle */}
                <div className="mt-3 mb-3">
                  <span
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold"
                    style={{
                      backgroundColor: 'var(--theme-primary-background)',
                      color: 'var(--theme-primary)',
                    }}
                  >
                    <Icon name="zap" className="h-4 w-4" />
                    {isQuote
                      ? 'Créditos sob demanda'
                      : `${formatCredits(plan.creditsPerCycle)} créditos/mês`}
                  </span>
                </div>

                {/* Price */}
                <div className="flex items-baseline justify-center gap-1">
                  <span
                    className="text-4xl font-extrabold tracking-tight"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    {isQuote
                      ? 'Sob consulta'
                      : formatCurrency(billingCycle === 'yearly' ? price.yearly : price.monthly, price.currency)}
                  </span>
                  {!isQuote && (
                    <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                      {billingCycle === 'yearly' ? '/ano' : '/mês'}
                    </span>
                  )}
                </div>

                {/* Features */}
                <ul className="flex flex-col gap-3 text-left mt-5 mb-5 flex-1">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <Icon
                        name="check"
                        className="h-[17px] w-[17px] flex-shrink-0 mt-0.5"
                        style={{ color: 'var(--theme-success-color)' }}
                      />
                      <span style={{ color: 'var(--theme-card-text)' }}>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div className="mt-auto space-y-2">
                  {isCurrent ? (
                    <Button
                      variant="secondary"
                      size="lg"
                      fullWidth
                      disabled
                      data-testid={`plan-cta-${plan.id}`}
                    >
                      Plano Atual
                    </Button>
                  ) : plan.status === 'active' ? (
                    <Button
                      variant={isPopular ? 'primary' : 'secondary'}
                      size="lg"
                      fullWidth
                      onClick={() => handleSelectPlan(plan)}
                      disabled={isChangingPlan}
                      data-testid={`plan-cta-${plan.id}`}
                    >
                      {isChangingPlan
                        ? 'Processando...'
                        : hasSubscription
                          ? 'Trocar Plano'
                          : 'Assinar'}
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="lg"
                      fullWidth
                      disabled
                      data-testid={`plan-cta-${plan.id}`}
                    >
                      Indisponivel
                    </Button>
                  )}

                  {showPixButton && (
                    <Button
                      variant="primary"
                      size="lg"
                      fullWidth
                      onClick={() => setIsUpgradePixModalOpen(true)}
                      data-testid={`plan-pix-${plan.id}`}
                    >
                      Gerar PIX
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* No Plans Message */}
        {plans && plans.length === 0 && (
          <div
            className="rounded-2xl text-center py-12"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              border: '1px solid var(--theme-card-border)',
            }}
            data-testid="plans-empty"
          >
            <Icon
              name="inbox"
              className="h-12 w-12 mx-auto mb-4"
              style={{ color: 'var(--theme-muted-text)' }}
            />
            <p style={{ color: 'var(--theme-muted-text)' }}>
              Nenhum plano disponivel no momento
            </p>
          </div>
        )}
      </div>

      {/* Payment Method Modal */}
      {selectedPlan && (
        <PaymentMethodModal
          isOpen={isPaymentModalOpen}
          onClose={() => {
            setIsPaymentModalOpen(false);
            setSelectedPlan(null);
          }}
          planId={selectedPlan.id}
          planName={selectedPlan.name}
        />
      )}

      {/* Upgrade PIX Modal */}
      <PixQrCodeModal
        isOpen={isUpgradePixModalOpen}
        onClose={() => setIsUpgradePixModalOpen(false)}
      />
    </div>
  );
};

PlansPage.displayName = 'PlansPage';
